/**
 * Lobby Socket Service
 *
 * Socket.IO client for lobby namespace with typed events.
 * Handles real-time updates for battle list in the lobby.
 */

import { io, Socket } from 'socket.io-client';
import { getApiUrl } from '../config/api';
import { getAuthToken } from './auth-token';

/**
 * Battle data structure from lobby events
 */
export interface LobbyBattle {
  battleId: string;
  battleKey?: string;              // DEPRECATED: Use battleId for joining
  battleName: string;
  status: 'waiting' | 'starting' | 'active' | 'completed';
  isPublic: boolean;
  creatorDisplayName: string;      // NEW: Creator's display name for easy access
  createdBy: string;               // NEW: Creator's userId
  players: {
    host: {
      userId: string;
      displayName: string;
      joinedAt: string;
    };
    opponent?: {
      userId: string;
      displayName: string;
      joinedAt: string;
    };
  };
  createdAt: string;
}

/**
 * Enhanced battle completed event data for community battle feed
 */
export interface BattleCompletedEvent {
  battleId: string;
  battleName: string;
  completedAt: string;
  player1DisplayName: string;
  player2DisplayName: string;
  winner: 'player1' | 'player2' | 'draw' | string;
  reason: string;
  duration: number;
}

/**
 * Lobby Socket Client
 *
 * Singleton Socket.IO client for real-time lobby updates.
 * Connects to /lobby namespace and handles battle list events.
 *
 * @example
 * ```typescript
 * const lobbySocket = new LobbySocket();
 * lobbySocket.connect();
 * lobbySocket.onBattles((battles) => {
 *   console.log('Received battles:', battles);
 * });
 * lobbySocket.subscribe();
 * ```
 */
export class LobbySocket {
  private socket: Socket | null = null;

  // Store callbacks to register when socket connects
  private pendingCallbacks: {
    battles?: (battles: LobbyBattle[]) => void;
    battleCreated?: (battle: LobbyBattle) => void;
    battleFilled?: (data: { battleId: string }) => void;
    battleCancelled?: (data: { battleId: string }) => void;
    battleCompleted?: (data: BattleCompletedEvent) => void;
    battleAbandoned?: (data: { battleId: string }) => void;
  } = {};

  /**
   * Connect to the lobby namespace
   * Creates Socket.IO connection with auth token
   */
  connect(): void {
    // Don't create multiple connections
    if (this.socket?.connected) {
      return;
    }

    const url = `${getApiUrl()}/lobby`;

    this.socket = io(url, {
      auth: async (callback) => {
        try {
          const token = await getAuthToken();
          callback({ token });
        } catch (error) {
          console.error('Failed to get auth token:', error);
          callback({});
        }
      },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5
    });

    // Register any pending callbacks that were set before connect()
    this.registerPendingCallbacks();

    // Auto-subscribe on connect
    this.socket.on('connect', () => {
      this.subscribe();
    });
  }

  /**
   * Register callbacks that were set before socket was created
   */
  private registerPendingCallbacks(): void {
    if (!this.socket) return;

    if (this.pendingCallbacks.battles) {
      this.socket.on('lobby:battles', this.pendingCallbacks.battles);
    }
    if (this.pendingCallbacks.battleCreated) {
      this.socket.on('lobby:battle-created', this.pendingCallbacks.battleCreated);
    }
    if (this.pendingCallbacks.battleFilled) {
      this.socket.on('lobby:battle-filled', this.pendingCallbacks.battleFilled);
    }
    if (this.pendingCallbacks.battleCancelled) {
      this.socket.on('lobby:battle-cancelled', this.pendingCallbacks.battleCancelled);
    }
    if (this.pendingCallbacks.battleCompleted) {
      this.socket.on('lobby:battle-completed', this.pendingCallbacks.battleCompleted);
    }
    if (this.pendingCallbacks.battleAbandoned) {
      this.socket.on('lobby:battle-abandoned', this.pendingCallbacks.battleAbandoned);
    }
  }

  /**
   * Disconnect from the lobby
   * Unsubscribes and closes Socket.IO connection
   */
  disconnect(): void {
    if (this.socket) {
      this.unsubscribe();
      this.socket.disconnect();
      this.socket = null; // Clear reference to allow reconnection
    }
  }

  /**
   * Subscribe to lobby updates
   * Emits 'lobby:subscribe' event to start receiving battle list updates
   */
  subscribe(): void {
    if (this.socket) {
      this.socket.emit('lobby:subscribe');
    }
  }

  /**
   * Unsubscribe from lobby updates
   * Emits 'lobby:unsubscribe' event to stop receiving battle list updates
   */
  unsubscribe(): void {
    if (this.socket) {
      this.socket.emit('lobby:unsubscribe');
    }
  }

  /**
   * Listen for full battle list updates
   *
   * @param callback - Called when 'lobby:battles' event received
   *
   * @example
   * ```typescript
   * lobbySocket.onBattles((battles) => {
   *   setBattles(battles);
   * });
   * ```
   */
  onBattles(callback: (battles: LobbyBattle[]) => void): void {
    // Remove previous listener to prevent accumulation
    if (this.socket && this.pendingCallbacks.battles) {
      this.socket.off('lobby:battles', this.pendingCallbacks.battles);
    }
    // Store callback for registration after connect()
    this.pendingCallbacks.battles = callback;
    // If socket already exists, register immediately
    if (this.socket) {
      this.socket.on('lobby:battles', callback);
    }
  }

  /**
   * Listen for new battle created events
   *
   * @param callback - Called when 'lobby:battle-created' event received
   *
   * @example
   * ```typescript
   * lobbySocket.onBattleCreated((battle) => {
   *   setBattles(prev => [...prev, battle]);
   * });
   * ```
   */
  onBattleCreated(callback: (battle: LobbyBattle) => void): void {
    if (this.socket && this.pendingCallbacks.battleCreated) {
      this.socket.off('lobby:battle-created', this.pendingCallbacks.battleCreated);
    }
    this.pendingCallbacks.battleCreated = callback;
    if (this.socket) {
      this.socket.on('lobby:battle-created', callback);
    }
  }

  /**
   * Listen for battle filled events (opponent joined)
   *
   * @param callback - Called when 'lobby:battle-filled' event received
   *
   * @example
   * ```typescript
   * lobbySocket.onBattleFilled((data) => {
   *   setBattles(prev => prev.filter(b => b.battleId !== data.battleId));
   * });
   * ```
   */
  onBattleFilled(callback: (data: { battleId: string }) => void): void {
    if (this.socket && this.pendingCallbacks.battleFilled) {
      this.socket.off('lobby:battle-filled', this.pendingCallbacks.battleFilled);
    }
    this.pendingCallbacks.battleFilled = callback;
    if (this.socket) {
      this.socket.on('lobby:battle-filled', callback);
    }
  }

  /**
   * Listen for battle cancelled events
   *
   * @param callback - Called when 'lobby:battle-cancelled' event received
   *
   * @example
   * ```typescript
   * lobbySocket.onBattleCancelled((data) => {
   *   setBattles(prev => prev.filter(b => b.battleId !== data.battleId));
   * });
   * ```
   */
  onBattleCancelled(callback: (data: { battleId: string }) => void): void {
    if (this.socket && this.pendingCallbacks.battleCancelled) {
      this.socket.off('lobby:battle-cancelled', this.pendingCallbacks.battleCancelled);
    }
    this.pendingCallbacks.battleCancelled = callback;
    if (this.socket) {
      this.socket.on('lobby:battle-cancelled', callback);
    }
  }

  /**
   * Listen for battle completed events (combat finished)
   * Event includes full battle result data for community battle feed
   *
   * @param callback - Called when 'lobby:battle-completed' event received
   *
   * @example
   * ```typescript
   * lobbySocket.onBattleCompleted((data) => {
   *   // data includes: battleId, battleName, completedAt, player1DisplayName,
   *   // player2DisplayName, winner, reason, duration
   *   setBattles(prev => prev.filter(b => b.battleId !== data.battleId));
   *   addToRecentBattles(data);
   * });
   * ```
   */
  onBattleCompleted(callback: (data: BattleCompletedEvent) => void): void {
    if (this.socket && this.pendingCallbacks.battleCompleted) {
      this.socket.off('lobby:battle-completed', this.pendingCallbacks.battleCompleted);
    }
    this.pendingCallbacks.battleCompleted = callback;
    if (this.socket) {
      this.socket.on('lobby:battle-completed', callback);
    }
  }

  /**
   * Listen for battle abandoned events (all players disconnected)
   *
   * @param callback - Called when 'lobby:battle-abandoned' event received
   *
   * @example
   * ```typescript
   * lobbySocket.onBattleAbandoned((data) => {
   *   setBattles(prev => prev.filter(b => b.battleId !== data.battleId));
   * });
   * ```
   */
  onBattleAbandoned(callback: (data: { battleId: string }) => void): void {
    if (this.socket && this.pendingCallbacks.battleAbandoned) {
      this.socket.off('lobby:battle-abandoned', this.pendingCallbacks.battleAbandoned);
    }
    this.pendingCallbacks.battleAbandoned = callback;
    if (this.socket) {
      this.socket.on('lobby:battle-abandoned', callback);
    }
  }

  /**
   * Get current connection status
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }
}
